import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../../../context';
import {
  StoryCardLimit,
  StoryCardOrder,
  StoryDisplayItemUpdatedTime,
} from '../../../../../services';
import {
  FeedFollowBehavior,
  FilterGroupItemsAlignment,
  FilterValue,
  optArrItem,
  RestrictDuplicateUrlOfCardsInTimeFrame,
  useFilter,
  useRestrictDuplicateUrlOfCardsInTimeFrame,
} from '../../../../../shared';

export enum OtherProps {
  shouldSyncItemPositionsOnConnectedStories = 'shouldSyncItemPositionsOnConnectedStories',
}

interface UseCreateStoryOtherFilterArgs {
  filter: FilterValue[];
  showFeedFollowBehavior: boolean;
}

export const useCreateStoryOtherFilter = ({
  filter,
  showFeedFollowBehavior,
}: UseCreateStoryOtherFilterArgs) => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'createStorySettings' });

  const { getGroupValue } = useFilter();

  const { options: restrictDuplicateUrlOfCardsInTimeFrameOptions } =
    useRestrictDuplicateUrlOfCardsInTimeFrame();

  const { userInfo, userProfile } = useContext(UserContext);

  const { channelRestrictDuplicateUrlOfCardsGQL } = userInfo.userData;

  const { isStaff } = userProfile;

  const restrictDuplicateDisabled = useMemo(
    () => channelRestrictDuplicateUrlOfCardsGQL !== RestrictDuplicateUrlOfCardsInTimeFrame.NEVER,
    [channelRestrictDuplicateUrlOfCardsGQL]
  );

  const restrictDuplicateValue = useMemo(
    () =>
      getGroupValue({ filter, groupId: 'shouldRestrictDuplicateUrlOfCardsInTimeFrame' }) ??
      RestrictDuplicateUrlOfCardsInTimeFrame.NEVER,
    [filter, getGroupValue]
  );

  const cardLimitValue = useMemo(
    () => getGroupValue({ filter, groupId: 'cardLimit' }) ?? StoryCardLimit.ONE_THOUSAND,
    [filter, getGroupValue]
  );

  const feedFollowBehaviorValue = useMemo(
    () => getGroupValue({ filter, groupId: 'feedFollowBehavior' }) ?? FeedFollowBehavior.MANDATORY,
    [filter, getGroupValue]
  );

  return {
    groups: [
      {
        id: 'shouldSyncItemPositionsOnConnectedStories',
        multiCheck: true,
        items: [
          {
            id: OtherProps.shouldSyncItemPositionsOnConnectedStories,
            label: ctxT('sync-positions'),
            value: OtherProps.shouldSyncItemPositionsOnConnectedStories,
          },
        ],
      },
      {
        id: 'displayItemUpdatedTime',
        title: ctxT('time-stamp'),
        itemsAlignment: FilterGroupItemsAlignment.ROW,
        items: [
          {
            id: StoryDisplayItemUpdatedTime.POSTED_TIME,
            label: t('common.posted-time'),
            value: StoryDisplayItemUpdatedTime.POSTED_TIME,
          },
          {
            id: StoryDisplayItemUpdatedTime.UPDATED,
            label: t('common.updated-time'),
            value: StoryDisplayItemUpdatedTime.UPDATED,
          },
        ],
      },
      {
        id: 'cardOrder',
        title: ctxT('card-order'),
        itemsAlignment: FilterGroupItemsAlignment.ROW,
        items: [
          {
            id: StoryCardOrder.MANUAL,
            label: ctxT('order-manual'),
            value: StoryCardOrder.MANUAL,
          },
          {
            id: StoryCardOrder.POSTED_TIME,
            label: t('common.posted-time'),
            value: StoryCardOrder.POSTED_TIME,
          },
        ],
      },
      {
        id: 'shouldRestrictDuplicateUrlOfCardsInTimeFrame',
        title: ctxT('avoid-duplicates'),
        value: restrictDuplicateValue,
        dropdown: {
          disabled: restrictDuplicateDisabled,
          options: restrictDuplicateUrlOfCardsInTimeFrameOptions,
        },
      },
      ...optArrItem(isStaff, {
        id: 'cardLimit',
        title: ctxT('card-limit'),
        value: cardLimitValue,
        dropdown: {
          options: [
            { id: StoryCardLimit.ONE_THOUSAND, title: 1000 },
            { id: StoryCardLimit.TWO_THOUSANDS, title: 2000 },
            { id: StoryCardLimit.FIVE_THOUSANDS, title: 5000 },
            { id: StoryCardLimit.TEN_THOUSANDS, title: 10000 },
            { id: StoryCardLimit.TWENTY_THOUSANDS, title: 20000 },
            { id: StoryCardLimit.UNLIMITED, title: t('common.unlimited') },
          ],
        },
      }),
      ...optArrItem(showFeedFollowBehavior, {
        id: 'feedFollowBehavior',
        title: t('common.feed-follow-behavior'),
        value: feedFollowBehaviorValue,
        dropdown: {
          options: Object.values(FeedFollowBehavior).map((value) => ({
            id: value,
            title: t(`feedFollowBehavior.${value}`),
            hint: t(`feedFollowBehaviorHints.${value}`),
          })),
        },
      }),
    ],
  };
};
